import React from 'react';

import { useTranslation } from '@/i18n';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { Selector } from '@/components/ui/inputs/selector';
import { Checkbox } from '@/components/ui/inputs';
import type { CheckInLocationItem } from '@/api/domains/checkInLocation';
import { useLocationFormPanel } from '@/hooks/checkInLocation/useLocationFormPanel';


interface LocationFormPanelProps {
  record?: CheckInLocationItem;
  onSubmit?: () => void;
  readOnly?: boolean;
}

export const LocationFormPanel: React.FC<LocationFormPanelProps> = ({
  record,
  onSubmit,
  readOnly,
}) => {
  const { t: translate } = useTranslation('checkInLocation');

  const {
    nameValue,
    addressValue,
    regionValue,
    communeValue,
    isRemoteWorkValue,
    regionOptions,
    communeOptions,
    isLoadingRegions,
    isLoadingCommunes,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    isEditMode,
    errors,
    isSubmitted,
    handleNameChange,
    handleAddressChange,
    handleRegionChange,
    handleCommuneChange,
    handleRemoteWorkChange,
    handleSubmit,
  } = useLocationFormPanel({ record, onSubmit, readOnly });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="location-form-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('form.fields.name')}
          placeholder={translate('form.placeholders.name')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.name?.message && isSubmitted
              ? translate(String(errors.name.message))
              : undefined
          }
          data-testid="location-name-input"
        />

        <TextArea
          label={translate('form.fields.address')}
          placeholder={translate('form.placeholders.address')}
          value={addressValue}
          onChange={handleAddressChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.address && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.address?.message && isSubmitted
              ? translate(String(errors.address.message))
              : undefined
          }
          rows={3}
          data-testid="location-address-input"
        />

        <Selector
          label={translate('form.fields.region')}
          placeholder={translate('form.placeholders.region')}
          options={regionOptions}
          value={regionValue}
          onChange={handleRegionChange}
          disabled={effectiveReadOnly || isSubmitting || isLoadingRegions}
          data-testid="location-region-selector"
        />

        <Selector
          label={translate('form.fields.commune')}
          placeholder={translate('form.placeholders.commune')}
          options={communeOptions}
          value={communeValue}
          onChange={handleCommuneChange}
          disabled={effectiveReadOnly || isSubmitting || !regionValue || isLoadingCommunes}
          data-testid="location-commune-selector"
        />

        <Checkbox
          label={translate('form.fields.isRemoteWork')}
          checked={isRemoteWorkValue}
          onChange={handleRemoteWorkChange}
          disabled={effectiveReadOnly || isSubmitting}
          data-testid="location-remote-work-checkbox"
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action={isEditMode ? 'edit' : 'create'}>
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {isEditMode
                ? translate('form.titleEdit')
                : translate('form.titleCreate')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default LocationFormPanel;
